import { useEffect } from 'react';
import type { FlightFilterCabin } from '../results/filters/flight_filter_panel.js';
import type { FlightExplorerPageProps } from './flight_explorer_page.js';
import {
  explorerBusy,
  explorerRefreshing,
  legSearchParams,
  type ExplorerState,
} from './explorer_state.js';
import type { FlightPageInfo, FlightSearchFacets, FlightSearchParams } from './search_source.js';

/**
 * O seam de CABINE da coluna de cortes: `undefined` enquanto congelado — aí não há
 * busca nenhuma a oferecer.
 *
 * Todo controle daquela coluna fala com o servidor (os cortes viram `refine`), então
 * o que separa a cabine não é o servidor: é REFINAR contra REFAZER. Um corte refina
 * a busca em cena — mesmo `searchKey`, a perna segue `ready`, só a lista volta
 * refiltrada. A cabine refaz: busca nova no provider, `searchKey` novo, lista e
 * fases zeradas (e os cortes junto — `search_started` redefine o filtro). Ela nem
 * poderia cortar: cabine não existe na oferta (nem no segmento, nem na tarifa), só
 * no pedido. Ver `CabinSection`. O que estava em cena fica guardado: se a busca
 * nova falhar ou pousar vazia, a cena volta com o aviso (#3213 no multi, #6438 no
 * one_way/RT).
 *
 * Fora do componente porque ele vive no teto de complexidade — e fora do arquivo
 * (`explorer_seams.ts`) pelo teto de linhas dele.
 */
export function cabinSeam(
  state: ExplorerState,
  frozen: boolean,
  leg: number,
  research: (params: FlightSearchParams) => void,
  onCabinChange: FlightExplorerPageProps['onCabinChange'],
): FlightFilterCabin | undefined {
  if (frozen) return undefined;
  return {
    ...(state.request.cabin ? { value: state.request.cabin } : {}),
    busy: explorerBusy(state),
    ...(state.cabinPolicy ? { policy: state.cabinPolicy } : {}),
    onSearch: (cabin) => {
      // O host ouve ANTES da busca sair: a cabine é critério do pedido (como
      // rota e datas) e o link compartilhado tem de carregá-la mesmo que a
      // re-busca falhe — o reducer só a grava no pouso (#3239 C5).
      onCabinChange?.(cabin);
      research({ ...legSearchParams(state.request, leg), cabin });
    },
  };
}

/**
 * A re-busca da cabine voltou ao quadro anterior: o host ouve a cabine do pedido
 * RESTAURADO. Ele ouviu a tentativa antes de a busca sair (`cabinSeam`), e sem
 * este eco a URL e o link compartilhado ficavam com a cabine que falhou ou não
 * tinha voo — um reload re-buscaria exatamente o vazio de que a pessoa saiu (#6438).
 */
export function useRollbackCabinEcho(
  state: ExplorerState,
  onCabinChange: FlightExplorerPageProps['onCabinChange'],
): void {
  const rollback = state.legRollback;
  const cabin = state.request.cabin;
  useEffect(() => {
    if (rollback) onCabinChange?.(cabin);
  }, [rollback, cabin, onCabinChange]);
}

/**
 * O seam de PAGINAÇÃO da coluna (scroll infinito): quem pede a página
 * seguinte, se há uma, se ela está em voo, e o `total` do rodapé — as facetas do
 * refine quando existem, senão o `page.total` que o provider devolve.
 *
 * RT fase 2 (a volta) é a fase que o refine NÃO cobre — a âncora do servidor é
 * a ida —, então ali quem pagina é o return-search, na MESMA sessão da ida
 * escolhida; o resto pagina pelo refine. O freeze de refine da fase 2 NÃO entra
 * no `hasMore`: é exatamente onde a paginação por return-search vive.
 *
 * Fora do componente porque ele vive no teto de complexidade (mesma razão de
 * `cabinSeam`).
 */
export function loadMoreSeam(
  state: ExplorerState,
  readOnly: boolean | undefined,
  serverFilter: { loadMore: () => void; refining: boolean; facets?: FlightSearchFacets },
  io: { loadMoreReturns: () => void },
): { loadMore: () => void; hasMore: boolean; loadingMore: boolean; total?: number } {
  const activeLegState = state.legs[state.activeLeg];
  const activePage = activeLegState?.phase === 'ready' ? activeLegState.page : undefined;
  const returnPhase = state.request.mode === 'round_trip' && state.activeLeg === 1;
  const total = serverFilter.facets?.total ?? activePage?.total;
  return {
    loadMore: returnPhase ? io.loadMoreReturns : serverFilter.loadMore,
    hasMore: canOfferNextPage(state, readOnly, serverFilter.refining, activePage),
    loadingMore: activeLegState?.phase === 'ready' && Boolean(activeLegState.loadingMore),
    ...(total !== undefined ? { total } : {}),
  };
}

/**
 * A sentinela tem próxima página a oferecer? Precisa de `hasNext` na página em
 * cena e de uma lista VIVA (nada de congelada, confirmada ou sendo re-buscada).
 *
 * `refining` desmonta a sentinela: o `inert` da lista barra o clique mas NÃO o
 * IntersectionObserver, e ela pediria sozinha a página seguinte do corte
 * ANTERIOR — a resposta pousaria concatenada numa lista que o refine em voo já
 * vai substituir. Na fase 2 do RT o refine é congelado (`refining` nunca acende),
 * e é exatamente lá que a paginação por return-search vive.
 *
 * Fora de `loadMoreSeam` porque ele vive no teto de complexidade.
 */
function canOfferNextPage(
  state: ExplorerState,
  readOnly: boolean | undefined,
  refining: boolean,
  activePage: FlightPageInfo | undefined,
): boolean {
  if (!activePage?.hasNext || refining) return false;
  return !readOnly && !state.confirmed && !explorerRefreshing(state);
}
